/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Box, BoxSection } from 'components/_new/Box';
import { Button } from 'components/_new/Button';
import { Link } from 'components/_new/Link';
import { classNames } from 'primereact/utils';
import { ReactComponent as FilePdfSVG } from 'assets/icons/new/file-pdf.svg';
import { ReactComponent as FileExternalSVG } from 'assets/icons/new/file-external.svg';
import { ReactComponent as FileEditSVG } from 'assets/icons/new/file-edit.svg';
import { ReactComponent as EyeSVG } from 'assets/icons/new/eye.svg';
import { ReactComponent as VideoSVG } from 'assets/icons/new/video.svg';
import { ReactComponent as FileTextSVG } from 'assets/icons/new/file-text.svg';
import { ReactComponent as TrashSVG } from 'assets/icons/new/trash.svg';
import { SessionFile, UserFileType } from 'types/SessionFile';
import { RouterUrlParams } from 'App';
import { usePanel } from 'contexts/panelContext';
import { usePair } from 'contexts/pairContext';
import { Icon } from 'components/_new/Icon';
import { Tag } from 'components/_new/Tag';

type NoteItemProps = {
	data: SessionFile;
	onPreviewClick?: () => void;
	onEditClick?: () => void;
	onDeleteClick?: () => void;
	bordered?: boolean;
	showButtonsOnHover?: boolean;
	slim?: boolean;
	displaySessionNumberInfo?: boolean;
	defaultOpenInPreview?: boolean;
};

export const NoteItem = ({
	data,
	onPreviewClick,
	onEditClick,
	onDeleteClick,
	bordered = false,
	showButtonsOnHover = false,
	slim = false,
	displaySessionNumberInfo = false,
	defaultOpenInPreview = false,
}: NoteItemProps) => {
	const { t } = useTranslation();

	const { organizationName, programName, type } = useParams() as RouterUrlParams;

	const {
		pair: { id: pairId },
	} = usePair();
	const to = `/panel/${organizationName}/${programName}/${type}/pair/${pairId}/userfiles/display/${data.id}`;

	const getFileTypeIcon = (fileType: UserFileType) => {
		switch (fileType) {
			// case UserFileType.LINK:
			// 	return <Icon name="link" className="text-xl" />;
			case UserFileType.PDF:
				return <FilePdfSVG />;
			case UserFileType.FILE:
			case UserFileType.HTML:
				return <FileTextSVG />;
			case UserFileType.VIDEO:
				return <VideoSVG />;
			default:
				return <i className="fa-solid fa-file" />;
		}
	};
	const FileTypeIcon = () => getFileTypeIcon(data.type);

	return (
		<Box
			className={classNames('box-file-container align-items-center pl-3 pr-2', {
				'hover-focus-area-lg': showButtonsOnHover,
				'md:py-1': !slim,
				'py-0': slim,
			})}
			style={{ minHeight: '60px' }}
			variant={bordered ? 'white-bordered' : 'white'}
			vertical
		>
			<BoxSection
				className="flex-grow flex align-items-center overflow-hidden"
				contentClassName="w-full"
				noPad={slim}
			>
				<div className="flex align-items-center gap-3 w-full overflow-hidden">
					<span className="icon-container flex text-primary">
						<FileTypeIcon />
					</span>
					<div className="flex flex-column gap-1 overflow-hidden flex-grow">
						<div className="flex align-items-center gap-2 overflow-hidden">
							<Link
								to={
									defaultOpenInPreview && onPreviewClick
										? '#'
										: `/panel/${organizationName}/${programName}/${type}/pair/${pairId}/userfiles/display/${data.id}`
								}
								target={data.type === 'link' ? '_target' : undefined}
								noUnderline
								variant="grey"
								className="block text-l font-bold white-space-nowrap overflow-hidden text-overflow-ellipsis"
								onClick={(event) => {
									if (defaultOpenInPreview && onPreviewClick) {
										event.preventDefault();
										onPreviewClick();
									}
								}}
								scrollToTop={!(defaultOpenInPreview && onPreviewClick)}
							>
								{data.name}
							</Link>
							{displaySessionNumberInfo && (
								<Tag variant="blue" min>
									{t('userPanel.sessions.sessionNo', { no: data.session?.sessionNumber })}
								</Tag>
							)}
						</div>
					</div>
				</div>
			</BoxSection>

			<BoxSection
				horizontal
				className="flex-shrink-0 right-col"
				contentClassName={classNames('flex flex-nowrap gap-1 align-items-center justify-content-end', {
					'buttons-visible': !showButtonsOnHover,
					'hover-focus-view': showButtonsOnHover,
				})}
				noPad={slim}
			>
				{onEditClick && (
					<Button
						label={t('actions.edit')}
						icon={<FileEditSVG />}
						iconClassName="icon-container icon-container-sm text-navy-blue"
						iconOnly
						variant="primary-text"
						variantSize="lg"
						noPad={slim}
						onClick={(event) => {
							event.preventDefault();
							onEditClick();
						}}
					/>
				)}
				{onDeleteClick && (
					<Button
						label={t('actions.remove')}
						icon={<TrashSVG />}
						iconClassName="icon-container icon-container-sm text-navy-blue"
						iconOnly
						variant="primary-text"
						variantSize="lg"
						noPad={slim}
						onClick={(event) => {
							event.preventDefault();
							onDeleteClick();
						}}
					/>
				)}
				{onPreviewClick && (
					<Button
						label={t('actions.preview')}
						icon={<EyeSVG />}
						iconClassName="icon-container icon-container-sm text-navy-blue"
						iconOnly
						variant="primary-text"
						variantSize="lg"
						noPad={slim}
						onClick={(event: any) => {
							event.preventDefault();
							onPreviewClick();
						}}
					/>
				)}
				<Button
					label={t('misc.openInNewTab')}
					icon={<FileExternalSVG />}
					iconClassName="icon-container icon-container-sm text-navy-blue"
					iconOnly
					variant="primary-text"
					variantSize="lg"
					noPad={slim}
					onClick={() => {
						const absoluteUrl = to.startsWith('/')
							? `${window.location.origin}${to}`
							: `${window.location.origin}/${to}`;
						window.open(absoluteUrl, '_blank');
					}}
				/>
			</BoxSection>
		</Box>
	);
};
